import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { BaseSubsectionAdapter } from 'components/Assessments/adapters/BaseSubsectionAdapter'
import { AdapterOptions, FlatAnswerMutation } from 'components/Assessments/adapters/types'
import {
  convertDiagnosesFlatAnswersToFormData,
  convertOtherDiagnosesToFlatAnswers,
  convertPrimaryDiagnosisToFlatAnswers
} from 'components/Assessments/helpers.uncontrolled'
import { getAnswerValuesFromItems } from 'components/Assessments/helpers'
import { SocFormData } from 'components/Assessments/SocAssessment/types/SocFormData'
import { SocAssessmentQuestionnaireItem } from 'components/Assessments/SocAssessment/types/questionnaire'
import { SocFormField } from 'components/Assessments/SocAssessment/types/SocFormField'

/**
 * Background subsection adapter
 * Handles diagnoses, equipment, allergies, and other background medical info (12 fields)
 */
export class BackgroundAdapter extends BaseSubsectionAdapter<SocFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<SocFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    // Extract diagnosis data using existing helper
    const { primaryDiagnosis, otherDiagnoses } = convertDiagnosesFlatAnswersToFormData(
      getAnswerValuesFromItems(
        flatAnswersArray,
        SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_PRIMARY_DIAGNOSIS
      ),
      getAnswerValuesFromItems(
        flatAnswersArray,
        SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_OTHER_DIAGNOSIS
      )
    )

    // Direct field extractions
    const backgroundEquipment = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_EQUIPMENT
    )

    const backgroundEquipmentOther = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_EQUIPMENT_OTHER
    )?.at(0)

    const backgroundDmeProvider = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_DME_PROVIDER
    )?.at(0)

    const backgroundAllergies = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_ALLERGIES
    )

    const backgroundAllergiesNotes = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_ALLERGIES_NOTES
    )?.at(0)

    const backgroundWaivers = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_WAIVERS
    )

    const backgroundAdditionalServices = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_ADDITIONAL_SERVICES
    )

    const backgroundPrognosis = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_PROGNOSIS
    )?.at(0)

    const backgroundDnr = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_DNR
    )?.at(0)

    const backgroundAdvanceDirectives = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_ADVANCE_DIRECTIVES
    )?.at(0)

    const backgroundAdvanceDirectivesNotes = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_ADVANCE_DIRECTIVES_NOTES
    )?.at(0)

    return {
      [SocFormField.BACKGROUND_PRIMARY_DIAGNOSIS]: primaryDiagnosis,
      [SocFormField.BACKGROUND_OTHER_DIAGNOSIS]: otherDiagnoses,
      [SocFormField.BACKGROUND_EQUIPMENT]: backgroundEquipment,
      [SocFormField.BACKGROUND_EQUIPMENT_OTHER]: backgroundEquipmentOther,
      [SocFormField.BACKGROUND_DME_PROVIDER]: backgroundDmeProvider,
      [SocFormField.BACKGROUND_ALLERGIES]: backgroundAllergies,
      [SocFormField.BACKGROUND_ALLERGIES_NOTES]: backgroundAllergiesNotes,
      [SocFormField.BACKGROUND_WAIVERS]: backgroundWaivers,
      [SocFormField.BACKGROUND_ADDITIONAL_SERVICES]: backgroundAdditionalServices,
      [SocFormField.BACKGROUND_PROGNOSIS]: backgroundPrognosis,
      [SocFormField.BACKGROUND_DNR]: backgroundDnr,
      [SocFormField.BACKGROUND_ADVANCE_DIRECTIVES]: backgroundAdvanceDirectives,
      [SocFormField.BACKGROUND_ADVANCE_DIRECTIVES_NOTES]: backgroundAdvanceDirectivesNotes
    }
  }

  buildFlatAnswers(): Array<{ linkId: string; value: unknown }> {
    const mutations: FlatAnswerMutation[] = []
    if (this.formDataFieldExists(SocFormField.BACKGROUND_PRIMARY_DIAGNOSIS)) {
      mutations.push({
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_PRIMARY_DIAGNOSIS,
        value: convertPrimaryDiagnosisToFlatAnswers(
          this.getFormDataField(SocFormField.BACKGROUND_PRIMARY_DIAGNOSIS)
        )
      })
    }
    if (this.formDataFieldExists(SocFormField.BACKGROUND_OTHER_DIAGNOSIS)) {
      mutations.push({
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_OTHER_DIAGNOSIS,
        value: convertOtherDiagnosesToFlatAnswers(
          this.getFormDataField(SocFormField.BACKGROUND_OTHER_DIAGNOSIS) ?? []
        )
      })
    }
    if (this.formDataFieldExists(SocFormField.BACKGROUND_EQUIPMENT_OTHER)) {
      mutations.push({
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_EQUIPMENT_OTHER,
        value: this.getFormDataField(SocFormField.BACKGROUND_EQUIPMENT_OTHER)
      })
    }
    if (this.formDataFieldExists(SocFormField.BACKGROUND_ADVANCE_DIRECTIVES_NOTES)) {
      mutations.push({
        linkId:
          SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_ADVANCE_DIRECTIVES_NOTES,
        value: this.getFormDataField(SocFormField.BACKGROUND_ADVANCE_DIRECTIVES_NOTES)
      })
    }

    if (this.formDataFieldExists(SocFormField.BACKGROUND_EQUIPMENT)) {
      mutations.push({
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_EQUIPMENT,
        value: this.getFormDataField(SocFormField.BACKGROUND_EQUIPMENT)
      })
    }
    if (this.formDataFieldExists(SocFormField.BACKGROUND_DME_PROVIDER)) {
      mutations.push({
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_DME_PROVIDER,
        value: this.getFormDataField(SocFormField.BACKGROUND_DME_PROVIDER)
      })
    }
    mutations.push(
      ...[
        {
          linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_ALLERGIES,
          value: this.getFormDataField(SocFormField.BACKGROUND_ALLERGIES)
        },
        {
          linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_ALLERGIES_NOTES,
          value: this.getFormDataField(SocFormField.BACKGROUND_ALLERGIES_NOTES)
        },
        {
          linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_WAIVERS,
          value: this.getFormDataField(SocFormField.BACKGROUND_WAIVERS)
        },
        {
          linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_ADDITIONAL_SERVICES,
          value: this.getFormDataField(SocFormField.BACKGROUND_ADDITIONAL_SERVICES)
        },
        {
          linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_PROGNOSIS,
          value: this.getFormDataField(SocFormField.BACKGROUND_PROGNOSIS)
        },
        {
          linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_DNR,
          value: this.getFormDataField(SocFormField.BACKGROUND_DNR)
        },
        {
          linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_ADVANCE_DIRECTIVES,
          value: this.getFormDataField(SocFormField.BACKGROUND_ADVANCE_DIRECTIVES)
        }
      ]
    )
    return mutations
  }
}
